<template>
  <div class="tags-manager">
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-skeleton width="100%"></b-skeleton>
      </template>

      <h6 class="dashboard-pages__title" v-if="title">{{ title }}</h6>
      <div class="no-tags" v-if="!showTags">
        <b-alert variant="info" show>
          Tags allow you to categorise and group your entries.
        </b-alert>
        <a href="#" @click="showAddTags = true">+ Add your first tag</a>
      </div>
      <div class="has-tags mt-2" v-if="showTags">
        <b-form-tags placeholder="Add Tag..." v-model="tags">
          <template v-slot="{ inputId, tags }">
            <b-input-group>
              <b-input
                maxlength="50"
                ref="tagInput"
                v-model="newTag"
                :id="inputId"
                placeholder="Add tag..."
                @input="handleTagInput"
                @keydown.enter="addTag(newTag)"
              />
              <b-input-group-append>
                <b-button
                  @click="addTag(newTag)"
                  :disabled="isTagEmpty"
                  class="ovr-button__inverted"
                  variant="primary"
                >
                  Add
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <small>Characters remaining: {{ remainingCharacters }}</small>
            <div
              v-if="tags.length"
              class="tags-display mt-3"
              style="font-size: 1.5rem"
            >
              <b-form-tag
                v-for="tag in tags"
                @remove="removeTag(tag)"
                :key="tag"
                :title="tag"
                class="tag mr-1 mb-1"
              >
                {{ tag }}
              </b-form-tag>
            </div>
          </template>
        </b-form-tags>
      </div>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import Providers from "@/providers";

export default {
  name: "TagsManager",
  props: {
    title: {
      type: String,
      required: false,
    },
    provider: {
      type: String,
      required: true,
    },
    organisationId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      tags: [],
      showAddTags: false,
      loading: true,
      remainingCharacters: 50,
      isTagEmpty: true,
      newTag: "",
    };
  },
  watch: {
    organisationId(newOrgId, oldOrgId) {
      if (newOrgId !== oldOrgId && this.provider === "organisation") {
        this.loadTags();
      }
    },
  },
  computed: {
    showTags() {
      return this.tags.length > 0 || this.showAddTags;
    },
  },
  methods: {
    resetTag() {
      this.remainingCharacters = 50;
      this.newTag = "";
    },
    handleTagInput(string) {
      this.isTagEmpty = !string.trim().length;
      this.remainingCharacters = 50 - string.length;
    },
    async addTag(tag) {
      if (this.isTagEmpty || !tag.trim()) return;

      if (this.tags.includes(tag.trim())) {
        this.$error("Duplicate Tag", "This tag already exists.");
        this.resetTag();
        return;
      }

      this.tags.push(tag.trim());
      this.resetTag();
      this.save(true, true);
    },
    removeTag(tagToRemove) {
      this.tags = this.tags.filter((tag) => tag !== tagToRemove);
      this.save(true, false);
    },
    save(shouldHidePreviousToast, shouldFocusOnTagInput) {
      this.loading = true;
      this.getSaveFunction()(this.tags)
        .then(() => {
          this.$success(
            "Tags Saved",
            "Your tags have been saved successfully."
          );
        })
        .catch((resp) => {
          this.$httpError("Failed to save tags", resp);
        })
        .finally(() => {
          this.loading = false;

          if (shouldHidePreviousToast) {
            this.$hideToast();
            if (shouldFocusOnTagInput) {
              this.$nextTick(() => {
                if (this.$refs.tagInput) {
                  this.$refs.tagInput.focus();
                  this.isTagEmpty = true;
                }
              });
            }
          }
        });
    },
    getLoadFunction() {
      if (this.provider === "profile") {
        return () => Providers.profile.tags();
      } else if (this.provider === "organisation") {
        return () =>
          Providers.organisationManagement.getTags(this.organisationId);
      }
      throw new Error(`Unknown provider: ${this.provider}`);
    },
    getSaveFunction() {
      if (this.provider === "profile") {
        return (tags) => Providers.profile.updateTags(tags);
      } else if (this.provider === "organisation") {
        return (tags) =>
          Providers.organisationManagement.updateTags(
            this.organisationId,
            tags
          );
      }
      throw new Error(`Unknown provider: ${this.provider}`);
    },
    loadTags() {
      this.loading = true;
      this.getLoadFunction()()
        .then((tags) => {
          this.tags = tags || [];
        })
        .catch((err) => {
          this.$httpError("Failed to load tags", err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refresh() {
      this.loadTags();
    },
  },
  created() {
    this.loadTags();
  },
};
</script>

<style lang="scss" scoped>
.tags-display {
  display: flex;
  flex-wrap: wrap;
}

.no-tags {
  a {
    color: #007bff;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

.tag {
  background: $border-color;
}
</style>
